import type { UseFormReturnType } from '@mantine/form'
import type {
  FieldErrorProps,
  FormProps,
  LabelProps,
  RadioGroupProps,
  TextFieldProps,
  TextProps,
} from 'react-aria-components'
import {
  FieldError,
  Form,
  Label,
  RadioGroup,
  Text,
  TextField,
} from 'react-aria-components'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

type UIFormFieldInputProps = {
  value?: string
  defaultValue?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  error?: string
}

// ****************
// Form
// ****************

export type UIFormProps<T> = {
  onSubmit: ((values: T) => Promise<void>) | ((values: T) => void)
} & Omit<FormProps, 'onSubmit'>

export function UIForm<T>({
  form,
  onSubmit,
  ...rest
}: UIFormProps<T> & {
  form: UseFormReturnType<T>
}) {
  return <Form onSubmit={form.onSubmit(onSubmit)} {...rest} />
}

// ****************
// Label
// ****************

const uiFormLabelStyles = tv({
  base: 'text-sm font-medium text-gray-800 dark:text-gray-200',
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    color: {
      default: 'text-gray-800 dark:text-gray-200',
      muted: 'text-gray-600 dark:text-gray-400',
    },
    required: {
      true: 'after:content-["*"] after:text-red-500 after:ml-1',
    },
  },
  defaultVariants: {
    size: 'md',
    weight: 'medium',
    color: 'default',
    required: false,
  },
})

export type UIFormLabelProps = LabelProps &
  VariantProps<typeof uiFormLabelStyles>

export function UIFormLabel({
  size,
  weight,
  color,
  required,
  className,
  children,
  ...rest
}: UIFormLabelProps) {
  const styles = uiFormLabelStyles({ size, weight, color, required, className })

  return (
    <Label className={styles} {...rest}>
      {children}
    </Label>
  )
}

// ****************
// Text Field
// ****************

const uiFormTextFieldStyles = tv({
  variants: {
    layout: {
      vertical: 'flex flex-col gap-2',
      horizontal: 'flex flex-row gap-3',
    },
  },
})

export type UIFormTextFieldProps = {
  input: UIFormFieldInputProps
  children: (input: UIFormFieldInputProps) => React.ReactNode
} & Omit<TextFieldProps, 'isInvalid' | 'children'> &
  VariantProps<typeof uiFormTextFieldStyles>

export function UIFormTextField({
  input,
  children,
  layout,
  ...rest
}: UIFormTextFieldProps) {
  const isInvalid = input.error !== undefined

  const styles = uiFormTextFieldStyles({ layout })

  return (
    <TextField isInvalid={isInvalid} className={styles} {...rest}>
      {children(input)}
    </TextField>
  )
}

// ****************
// Radio Group
// ****************

export type UIFormRadioGroupProps = {
  input: UIFormFieldInputProps
  children: React.ReactNode
} & Omit<RadioGroupProps, 'value' | 'defaultValue' | 'children'>

export function UIFormRadioGroup({
  input,
  children,
  ...rest
}: UIFormRadioGroupProps) {
  const { value, defaultValue } = input

  return (
    <RadioGroup value={value} defaultValue={defaultValue} {...rest}>
      {children}
    </RadioGroup>
  )
}

// ****************
// Description Region
// ****************

const uiFormDescriptionStyles = tv({
  slots: {
    description: 'text-sm text-gray-600 dark:text-gray-400',
    error: 'text-sm text-red-600 dark:text-red-400',
  },
})

export type UIFormDescriptionProps = {
  input?: UIFormFieldInputProps
  children?: React.ReactNode
  textProps?: Omit<TextProps, 'children' | 'description'>
  errorProps?: Omit<FieldErrorProps, 'children'>
  className?: string
}

export function UIFormDescription({
  input,
  children,
  textProps,
  errorProps,
}: UIFormDescriptionProps) {
  const styles = uiFormDescriptionStyles()

  const { className: textClassName, ...textRest } = textProps || {}

  if (input?.error) {
    return (
      <FieldError className={styles.error()} {...errorProps}>
        {input.error}
      </FieldError>
    )
  }

  if (!children) {
    return null
  }

  return (
    <Text
      slot="description"
      className={styles.description({ className: textClassName })}
      {...textRest}
    >
      {children}
    </Text>
  )
}
